import React, { useCallback, useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { ArrowRight } from 'react-feather'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import FileUploaderWithActions from 'src/components/common/form-elements/FileUploaderWithActions'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { successToast, errorToast } from 'src/lib/utils/toasters'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { CSVPreview } from 'src/components/common/ui-preview/CSVPreview'
import { Info } from 'react-feather'
import { ErrorType } from 'src/lib/hooks/useCSVValidator'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { useRewardsPermissions } from 'src/lib/access-permission-hooks/useRewardsPermissions'
import useGTM from 'src/lib/hooks/useGTM'

const ISSUER_ID = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

const RewardsBulkUpload: React.FC<{}> = ({}) => {
  const [openTopUpDialog, setOpenTopUpDialog] = useState<boolean>(false)
  const [uploadedFileFormData, setUploadedFileFormData] = useState<any>()
  const [errorStatus, setErrorStatus] = useState<'CSV_HEADER_ERROR' | string>('')

  const { setLoaderProperties } = useLoader()
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  const { sendActivityLog } = useActivityLogs()
  const { canEditSetupRewards } = useRewardsPermissions()
  const { sendGTMTrigger } = useGTM()
  const errorMessage = useMemo(() => {
    if (errorStatus === 'CSV_HEADER_ERROR') {
      return 'Format of file uploaded does not match the template, correct the format and try again'
    }
    if (errorStatus) {
      return errorStatus
    }
    return ''
  }, [errorStatus])

  const handleError = useCallback((error: string) => {
    if (error) {
      setErrorStatus(error)
    }
  }, [])

  const renderTopUpDialogContent = () => {
    return (
      <div className='flex flex-col gap-4 px-4'>
        <Typography variant='body2' sx={{ fontWeight: 400, color: 'text.header' }}>
          Sample CSV file format:
        </Typography>
        <CSVPreview
          columns={['country_code', 'phone_number', 'credit/debit', 'coins_to_adjust', 'expiry_in_days']}
          rows={[
            ['91', '9876543210', 'credit', '100', '30'],
            ['91', '9198765432', 'debit', '50', '15']
          ]}
        />
        <FileUploaderWithActions
          buttonText='Upload'
          type='.csv'
          size='medium'
          variant='outlined'
          color='primary'
          acceptedSize={104857600}
          uploadOnFileSave={false}
          placeholderText='Upload a CSV with wallet adjustments'
          setSelectedCSVFormData={value => {
            if (value === 'DELETED') {
              setErrorStatus('')
            }
            setUploadedFileFormData(value)
          }}
          onError={handleError}
          errorStatus={Boolean(errorStatus)}
          errorMessage={errorMessage}
          errorsToCheck={[ErrorType.FILE_SIZE_EXCEEDED, ErrorType.REWARDS]}
        />
        <div className=' bg-[#C7981F1A] flex flex-row justify-center items-center gap-1 w-fit p-2 rounded-lg border-yellow-400 border-1'>
          <Info size={16} className='pr-1' color='#C7981F' />
          <p className='text-sm font-normal text-coal-light'>Please ensure the uploaded file is in the right format.</p>
          <a
            className='text-sm font-semibold underline text-coal-dark decoration-gray-light decoration-[1.5px] underline-offset-4 text-nowrap'
            download
            href='https://dkuy6716075w5.cloudfront.net/sample.csv'
          >
            Download a sample file
          </a>
        </div>
      </div>
    )
  }

  const handleSaveTopUp = async () => {
    setLoaderProperties({ isLoaderVisible: true })
    if (!Boolean(uploadedFileFormData)) {
      errorToast('Please upload a CSV')
      setLoaderProperties({ isLoaderVisible: false })
      return
    }
    try {
      const response = await postRequest(
        `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration/adjust-points`,
        apiURI.REWARDS_ENGINE,
        uploadedFileFormData
      )
      if (Boolean((response as any)?.data?.success)) {
        const activityLogPayload = {
          entityName: EntityName.REWARDS,
          actionName: 'edited the configuration'
        }
        sendGTMTrigger('rewards_bulk_top_up_completed', 'save')
        sendActivityLog(activityLogPayload)
        successToast((response as any)?.data?.message)
        setOpenTopUpDialog(false)
        setUploadedFileFormData(null)
        setErrorStatus('')
      } else {
        if ((response as any)?.error?.includes('CSV file does not contain the required columns')) {
          setErrorStatus('CSV_HEADER_ERROR')
        } else {
          errorToast((response as any)?.error)
        }
      }
    } catch (e) {
      console.error(e)
      errorToast('Error while uploading file. Please try again.')
      setOpenTopUpDialog(false)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  return (
    <>
      <div className='flex items-center justify-between' onClick={() => setOpenTopUpDialog(canEditSetupRewards)}>
        <p>Credit or debit customer wallets in bulk</p>
        <ArrowRight size='16' className='font-medium text-[#6E6E6E] stroke-[2.5px] cursor-pointer' />
      </div>
      {Boolean(openTopUpDialog) && (
        <FloGenericDialog
          width={600}
          isOpen={openTopUpDialog}
          handleClose={() => {
            setUploadedFileFormData(null)
            setOpenTopUpDialog(false)
            setErrorStatus('')
          }}
          content={renderTopUpDialogContent()}
          title={'Top up'}
          referrer='rewards-bulk-top-up-adjust'
          jobButtonText={'Top up'}
          jobButtonDisabled={!Boolean(uploadedFileFormData) || Boolean(errorStatus)}
          closeButtonText={'Cancel'}
          handleJob={handleSaveTopUp}
          contentCss={{ padding: 0 }}
        />
      )}
    </>
  )
}

export default React.memo(RewardsBulkUpload)
